/**
 * client.ts
 *
 * Fetch wrapper for `/api/v1`. Normalizes the backend's `{error:{code,
 * message, detail}}` envelope into a typed ApiError, attaches the stored
 * JWT as a Bearer header (never cookies — taskpane webviews don't
 * reliably carry them), and — when VITE_MOCK_API=1 — delegates every call
 * to api/mock.ts instead of hitting the network at all.
 *
 * This module also owns the one place that touches localStorage for the
 * JWT (key "filer.jwt"), so useAuth.ts and this file can't drift on the
 * storage key.
 */
import { mockApi } from "./mock";
import {
  ApiError,
  type AdminSettings,
  type ApiErrorBody,
  type DevLoginResponse,
  type FilingGroupResult,
  type FilingPrecheckRequest,
  type FilingPrecheckResponse,
  type FilingRequest,
  type IndexStatus,
  type MeResponse,
  type ProjectSearchResponse,
  type RecentFilingsResponse,
  type SearchFilters,
  type Subscription,
  type SubscriptionListResponse,
  type UndoFilingTargetResponse,
  type UpdateAdminSettingsRequest,
} from "./types";

const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api/v1";
const MOCK_API = import.meta.env.VITE_MOCK_API === "1";

const TOKEN_STORAGE_KEY = "filer.jwt";

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

interface RequestOptions {
  method?: "GET" | "POST" | "DELETE" | "PUT";
  body?: unknown;
  /** Attach the Authorization header. Default true; dev-login is the one
   * call made before a token exists. */
  auth?: boolean;
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== "object" || value === null || !("error" in value)) {
    return false;
  }
  const err = (value as { error?: unknown }).error;
  return (
    typeof err === "object" &&
    err !== null &&
    "code" in err &&
    "message" in err
  );
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, auth = true } = options;
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (auth) {
    const token = getStoredToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    throw new ApiError(
      0,
      "NETWORK_ERROR",
      err instanceof Error ? err.message : "Network request failed",
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch {
    json = undefined;
  }

  if (!response.ok) {
    if (isApiErrorBody(json)) {
      throw new ApiError(
        response.status,
        json.error.code,
        json.error.message,
        json.error.detail,
      );
    }
    throw new ApiError(
      response.status,
      "UNKNOWN_ERROR",
      `Request failed with status ${response.status}`,
    );
  }

  return json as T;
}

/** Build the /projects/search query string, omitting empty filters entirely. */
function searchParams(query: string, filters?: SearchFilters): string {
  const params = new URLSearchParams({ q: query });
  if (filters?.divisions.length) {
    params.set("divisions", filters.divisions.join(","));
  }
  if (filters?.years.length) {
    params.set("years", filters.years.join(","));
  }
  if (filters?.pathPrefix) {
    params.set("path_prefix", filters.pathPrefix);
  }
  return params.toString();
}

export const api = {
  devLogin: (token: string): Promise<DevLoginResponse> =>
    MOCK_API
      ? mockApi.devLogin(token)
      : request("/auth/dev-login", {
          method: "POST",
          body: { token },
          auth: false,
        }),

  getMe: (): Promise<MeResponse> =>
    MOCK_API ? mockApi.me() : request("/me"),

  searchProjects: (
    query: string,
    filters?: SearchFilters,
  ): Promise<ProjectSearchResponse> =>
    MOCK_API
      ? mockApi.searchProjects(query, filters)
      : request(`/projects/search?${searchParams(query, filters)}`),

  getIndexStatus: (): Promise<IndexStatus> =>
    MOCK_API ? mockApi.indexStatus() : request("/index/status"),

  getAdminSettings: (): Promise<AdminSettings> =>
    MOCK_API ? mockApi.adminSettings() : request("/admin/settings"),

  updateAdminSettings: (
    body: UpdateAdminSettingsRequest,
  ): Promise<AdminSettings> =>
    MOCK_API
      ? mockApi.updateAdminSettings(body)
      : request("/admin/settings", { method: "PUT", body }),

  resetAdminSetting: (key: string): Promise<AdminSettings> =>
    MOCK_API
      ? mockApi.resetAdminSetting(key)
      : request(`/admin/settings/${encodeURIComponent(key)}`, {
          method: "DELETE",
        }),

  listSubscriptions: (): Promise<SubscriptionListResponse> =>
    MOCK_API ? mockApi.listSubscriptions() : request("/subscriptions"),

  subscribe: (projectId: number): Promise<Subscription> =>
    MOCK_API
      ? mockApi.subscribe(projectId)
      : request("/subscriptions", {
          method: "POST",
          body: { project_id: projectId },
        }),

  unsubscribe: (projectId: number): Promise<void> =>
    MOCK_API
      ? mockApi.unsubscribe(projectId)
      : request(`/subscriptions/${projectId}`, { method: "DELETE" }),

  precheckFiling: (
    req: FilingPrecheckRequest,
  ): Promise<FilingPrecheckResponse> =>
    MOCK_API
      ? mockApi.precheck(req)
      : request("/filings/precheck", { method: "POST", body: req }),

  createFiling: (req: FilingRequest): Promise<FilingGroupResult> =>
    MOCK_API
      ? mockApi.createFiling(req)
      : request("/filings", { method: "POST", body: req }),

  getRecentFilings: (): Promise<RecentFilingsResponse> =>
    MOCK_API ? mockApi.recentFilings() : request("/filings/recent"),

  undoFilingTarget: (targetId: number): Promise<UndoFilingTargetResponse> =>
    MOCK_API
      ? mockApi.undoTarget(targetId)
      : request(`/filings/targets/${targetId}/undo`, { method: "POST" }),
};
